/**
 * "AI design" popup of a composition card: describe the artwork, optionally
 * point at a logo among the order / customer attachments, generate, then put
 * the result into the composition or keep it as an order attachment.
 *
 * The backend enforces the printing technique (colour count, flat colours,
 * stroke width, zone proportions); this view only shows what it will enforce.
 */
define('ordersDetailCompositionAiDesignView',[
  'module',
  'underscore',
  'jquery',
  'bootbox',
  'settings',
  'dialogView',
  'template!ordersDetailCompositionAiDesignTpl'
], function (
  Module,
  _,
  $,
  bootbox,
  Settings,
  DialogView,
  aiDesignTpl
) {
  'use strict';

  function t(key, fallback) {
    return _.i18n('orders.detail.composition.aiDesign.' + key, null, fallback);
  }

  /**
   * Thumbnail of a reference, the way the attachments tab does it: PDF / AI /
   * EPS go through the file server's preview rendering, SVG is served as is,
   * rasters get a resized copy.
   */
  function thumbnailUrl(ref) {
    var link = ref.previewUrl || '';
    if (!link) {
      return '';
    }
    var name = ref.name || '';
    var ext = (ref.extension || (name.indexOf('.') > 0 ? name.split('.').pop() : '')).toLowerCase();
    if (ext === 'pdf' || ext === 'ai' || ext === 'eps') {
      return Settings.url('base', link);
    }
    if (ext === 'svg') {
      return Settings.url('base', link.replace('&action=preview', ''));
    }
    return Settings.url('base', link.replace('&action=preview', '&action=res'));
  }

  function errorText(xhr) {
    if (xhr && xhr.responseJSON && xhr.responseJSON.error) {
      return xhr.responseJSON.error;
    }
    if (xhr && xhr.status === 404) {
      return t('notFound', 'This composition no longer exists.');
    }
    return t('failed', 'The design could not be generated.');
  }

  Module.exports = DialogView.extend({
    template: aiDesignTpl,
    className: 'composition-ai-design',

    ui: {
      constraints: '.js-constraints',
      prompt: '.js-prompt',
      references: '.js-references',
      upload: '.js-upload',
      generate: '.js-generate',
      result: '.js-result'
    },

    events: {
      'input @ui.prompt': 'onPromptInput',
      'keydown @ui.prompt': 'onPromptKey',
      'click .js-reference': 'onReferenceClick',
      'change @ui.upload': 'onUploadChange',
      'click @ui.generate': 'onGenerate',
      'click .js-apply': 'onApply',
      'click .js-attach': 'onAttach',
      'click .js-again': 'onAgain'
    },

    /**
     * @param options.orderModel   the order (entities/orders), used for the calls
     * @param options.composition  the composition model of the card (CompositionJson)
     */
    initialize: function (options) {
      this.orderModel = options.orderModel;
      this.composition = options.composition;
      this.secId = this.composition.get('secId');
      this.references = [];
      this.selectedReference = null;
      this.result = null;
      this.busy = false;
    },

    serializeData: function () {
      var c = this.composition;
      var zone = [];
      if (c.get('paName')) {
        zone.push(c.get('paName'));
      }
      if (c.get('itName')) {
        zone.push(c.get('itName'));
      }
      var size = '';
      if (c.get('pzWidth') && c.get('pzHeight')) {
        size = c.get('pzWidth') + ' x ' + c.get('pzHeight') + ' ' + (c.get('pzUnit') || 'mm');
      }
      return {
        zoneLabel: zone.join(' · ') || t('title', 'AI design'),
        zoneSize: size
      };
    },

    onShow: function () {
      DialogView.prototype.onShow.apply(this, arguments);
      this.loadBrief();
    },

    onShown: function () {
      this.ui.prompt.focus();
    },

    //----------Brief----------------------------------------------------------//

    loadBrief: function (selectUid) {
      var self = this;
      this.orderModel.getCompositionDesignBrief(this.secId).done(function (brief) {
        self.renderConstraints(brief);
        self.references = brief.references || [];
        if (selectUid) {
          self.selectedReference = selectUid;
        } else if (self.selectedReference && !_.findWhere(self.references, {fileUid: self.selectedReference})) {
          self.selectedReference = null;
        }
        self.renderReferences();
        self.configured = brief.configured !== false;
        if (!self.configured) {
          self.ui.constraints.append('<div class="cad-error">' +
            _.escape(t('notConfigured', 'No OpenAI API key for this account: add it in Mailing → Settings.')) + '</div>');
        }
        self.updateGenerateState();
      }).fail(function (xhr) {
        self.ui.constraints.html('<div class="cad-error">' + _.escape(errorText(xhr)) + '</div>');
      });
    },

    renderConstraints: function (brief) {
      var lines = brief.constraints || [];
      if (!lines.length) {
        this.ui.constraints.html('<div class="cad-error">' +
          _.escape(t('noZone', 'The print zone of this composition could not be resolved.')) + '</div>');
        return;
      }
      var html = '<div class="cad-constraints-title">' +
        _.escape(t('constraintsTitle', 'The design will respect')) + '</div><ul>';
      _.each(lines, function (line) {
        // Keep the headline of each rule; the explanation after the first
        // sentence is for the model, not the operator.
        var headline = line.split('. ')[0];
        html += '<li title="' + _.escape(line) + '">' + _.escape(headline) + '</li>';
      });
      html += '</ul>';
      this.ui.constraints.html(html);
    },

    renderReferences: function () {
      var self = this;
      if (!this.references.length) {
        this.ui.references.html('<div class="cad-muted">' +
          _.escape(t('noReference', 'No attachment on the order or the customer yet.')) + '</div>');
        return;
      }
      var html = '<div class="cad-reference-item js-reference' + (this.selectedReference ? '' : ' selected') +
        '" data-uid=""><div class="cad-reference-none">' + _.escape(t('none', 'None')) + '</div></div>';
      _.each(this.references, function (ref) {
        var selected = ref.fileUid === self.selectedReference ? ' selected' : '';
        html += '<div class="cad-reference-item js-reference' + selected + '" data-uid="' + _.escape(ref.fileUid) +
          '" title="' + _.escape(ref.name || '') + '">' +
          '<img src="' + _.escape(thumbnailUrl(ref)) + '" alt="">' +
          '<span class="cad-reference-name">' + _.escape(ref.name || '') + '</span>' +
          '<span class="cad-reference-source">' +
            _.escape(ref.source === 'customer' ? t('sourceCustomer', 'customer') : t('sourceOrder', 'order')) +
          '</span></div>';
      });
      this.ui.references.html(html);
    },

    onReferenceClick: function (e) {
      var uid = $(e.currentTarget).attr('data-uid');
      this.selectedReference = uid || null;
      this.ui.references.find('.js-reference').removeClass('selected');
      $(e.currentTarget).addClass('selected');
    },

    onUploadChange: function (e) {
      var self = this;
      var file = e.target.files && e.target.files[0];
      if (!file) {
        return;
      }
      this.setBusy(true, t('uploading', 'Uploading…'));
      this.orderModel.uploadAttachment(file).done(function (attachment) {
        var uid = attachment && attachment.get ? attachment.get('fileUid') : null;
        self.loadBrief(uid);
      }).fail(function () {
        bootbox.alert(t('uploadFailed', 'The file could not be uploaded.'));
      }).always(function () {
        self.ui.upload.val('');
        self.setBusy(false);
      });
    },

    //----------Generation-----------------------------------------------------//

    onPromptInput: function () {
      this.updateGenerateState();
    },

    onPromptKey: function (e) {
      if (e.keyCode === 13 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.onGenerate();
      }
    },

    updateGenerateState: function () {
      var ready = !this.busy && this.configured !== false && $.trim(this.ui.prompt.val()).length > 0;
      this.ui.generate.prop('disabled', !ready);
    },

    setBusy: function (busy, label) {
      this.busy = busy;
      this.ui.generate.find('span').text(busy && label ? label : t('generate', 'Generate'));
      this.ui.upload.prop('disabled', busy);
      this.updateGenerateState();
    },

    onGenerate: function () {
      var self = this;
      var prompt = $.trim(this.ui.prompt.val());
      if (!prompt || this.busy || this.configured === false) {
        return;
      }
      this.setBusy(true, t('generating', 'Generating…'));
      this.ui.result.html('<div class="cad-loading cad-loading-big">' +
        _.escape(t('generatingHint', 'Drawing the design within the printing constraints — 30 to 90 seconds.')) + '</div>');
      this.orderModel.generateCompositionDesign(this.secId, prompt, this.selectedReference).done(function (result) {
        self.result = result;
        self.renderResult();
      }).fail(function (xhr) {
        self.result = null;
        self.ui.result.html('<div class="cad-error">' + _.escape(errorText(xhr)) + '</div>');
      }).always(function () {
        self.setBusy(false);
      });
    },

    renderResult: function () {
      var r = this.result;
      this.ui.result.html(
        '<div class="cad-result-card">' +
        '  <a class="cad-result-preview" href="' + _.escape(r.imageUrl) + '" target="_blank" rel="noopener noreferrer">' +
        '    <img src="' + _.escape(r.imageUrl) + '" alt="">' +
        '  </a>' +
        '  <div class="cad-result-actions">' +
        '    <button type="button" class="btn btn-primary js-apply">' +
        '      <i class="mdi-action-check-circle"></i> ' + _.escape(t('apply', 'Put it in the composition')) + '</button>' +
        '    <button type="button" class="btn btn-default js-attach">' +
        '      <i class="mdi-content-save"></i> ' + _.escape(t('attach', 'Save as order attachment')) + '</button>' +
        '    <button type="button" class="btn btn-default js-again">' +
        '      <i class="mdi-action-autorenew"></i> ' + _.escape(t('again', 'Generate another')) + '</button>' +
        '    <a class="cad-open" href="' + _.escape(r.imageUrl) + '" target="_blank" rel="noopener noreferrer">' +
        '      <i class="mdi-action-open-in-new"></i> ' + _.escape(t('open', 'Open full size')) + '</a>' +
        '  </div>' +
        '  <div class="cad-result-note">' +
             _.escape(t('applyNote', 'Putting it in the composition replaces the current artwork of this print zone.')) +
        '  </div>' +
        '</div>');
    },

    onAgain: function () {
      this.onGenerate();
    },

    onApply: function () {
      var self = this;
      if (!this.result || this.busy) {
        return;
      }
      var $apply = this.ui.result.find('.js-apply').prop('disabled', true);
      this.orderModel.applyAttachmentToComposition(this.secId, this.result.fileUid, this.result.fileName)
        .done(function () {
          self.orderModel.fetch();
          self.hide();
        })
        .fail(function (xhr) {
          $apply.prop('disabled', false);
          bootbox.alert(errorText(xhr));
        });
    },

    onAttach: function () {
      if (!this.result || this.busy) {
        return;
      }
      var $attach = this.ui.result.find('.js-attach').prop('disabled', true);
      this.orderModel.attachCompositionDesign(this.secId, this.result.fileUid, this.result.fileName)
        .done(function () {
          $attach.html('<i class="mdi-action-done"></i> ' + _.escape(t('attached', 'Saved in the attachments')));
        })
        .fail(function (xhr) {
          $attach.prop('disabled', false);
          bootbox.alert(errorText(xhr));
        });
    }
  });
});

